import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, Check, BookOpen, BarChart3, Shield, Loader2, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { platform, iapAvailable, purchase, restorePurchases } from "@/lib/iap";

const FEATURES = [
  { Icon: BookOpen, title: "Private journaling", body: "Private journal entries with mood tagging, kept out of public profiles and feeds." },
  { Icon: BarChart3, title: "Private weekly mood report", body: "A private reflection highlights gentle patterns without sending your journal to an AI provider." },
  { Icon: Sparkles, title: "Distraction-free mode", body: "An ad-free Calm experience with longer mindful-break sessions and a single-column reading view." },
  { Icon: Shield, title: "Advanced privacy", body: "Hide your activity completely from non-mutuals. Stricter DM filters." },
];

function checkoutIdempotencyKey(packageId) {
  const storageKey = `haeven-premium-checkout-${packageId}`;
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const generated = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(storageKey, generated);
  return generated;
}

function clearCheckoutIdempotencyKey(packageId) {
  if (packageId) {
    window.sessionStorage.removeItem(`haeven-premium-checkout-${packageId}`);
  }
}

export default function PremiumPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const track = useCallback((event, details = {}) => {
    const currentPlatform = platform();
    const billingModel = currentPlatform === "web"
      ? details.billing_model
      : "native_subscription";
    api.post("/premium/events", {
      event,
      platform: currentPlatform,
      billing_model: billingModel,
      package_id: details.package_id,
    }).catch((err) => console.warn("Monetization event failed", err));
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/premium/status");
      setStatus(r.data);
    } catch (err) {
      console.warn("Failed to load premium status", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const key = "haeven-calm-paywall-viewed";
    if (!window.sessionStorage.getItem(key)) {
      window.sessionStorage.setItem(key, "1");
      track("calm_paywall_viewed");
    }
  }, [track]);

  useEffect(() => {
    if (params.get("checkout") !== "canceled") return;
    const requestedPackage = params.get("package_id");
    const packageId = ["calm_monthly", "calm_yearly"].includes(requestedPackage)
      ? requestedPackage
      : undefined;
    clearCheckoutIdempotencyKey(packageId);
    track("calm_checkout_canceled", { package_id: packageId });
    toast.info("Checkout canceled. You can safely try again.");
    navigate("/premium", { replace: true });
  }, [params, navigate, track]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/premium/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Welcome to HAEVEN Premium 💛");
          if (r.data.package_id) {
            clearCheckoutIdempotencyKey(r.data.package_id);
          }
          track("calm_membership_activated");
          await refresh();
          await load();
          setPolling(false);
          navigate("/premium", { replace: true });
          return;
        }
        if (r.data.status === "expired") {
          clearCheckoutIdempotencyKey(r.data.package_id);
          track("calm_checkout_failed", {
            package_id: r.data.package_id,
            billing_model: r.data.billing_model,
          });
          toast.error("Checkout expired. Please try again.");
          setPolling(false);
          navigate("/premium", { replace: true });
          return;
        }
        if (attempts < 8) setTimeout(tick, 2000);
        else { setPolling(false); toast.warning("Still processing — refresh in a moment."); }
      } catch (err) {
        console.warn("Stripe status poll failed", err);
        setPolling(false);
      }
    };
    tick();
  }, [params, navigate, refresh, load, track]);

  const checkout = async (packageId) => {
    const selected = status?.packages?.[packageId];
    const billingModel = platform() === "web"
      ? selected?.web_billing_model
      : "native_subscription";
    track("calm_plan_selected", { package_id: packageId, billing_model: billingModel });
    setBusy(true);
    try {
      if (iapAvailable()) {
        track("calm_checkout_started", { package_id: packageId, billing_model: billingModel });
        const result = await purchase(packageId);
        if (result.active) {
          toast.success("Welcome to HAEVEN Premium");
          track("calm_membership_activated", { package_id: packageId, billing_model: billingModel });
          await refresh();
          await load();
        } else if (result.pending) {
          toast.warning("Payment received. Your Premium membership is still syncing—tap Restore Purchases shortly.");
          track("calm_activation_pending", { package_id: packageId, billing_model: billingModel });
        } else {
          toast.error("No active subscription was verified. Please try Restore Purchases.");
        }
        return;
      }
      track("calm_checkout_started", { package_id: packageId, billing_model: billingModel });
      const r = await api.post("/premium/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
        idempotency_key: checkoutIdempotencyKey(packageId),
      });
      window.location.href = r.data.url;
    } catch (e) {
      track("calm_checkout_failed", { package_id: packageId, billing_model: billingModel });
      toast.error(e.response?.data?.detail || e.message || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    track("calm_restore_started");
    setBusy(true);
    try {
      const result = await restorePurchases();
      if (result.active) {
        toast.success("Membership restored");
        track("calm_restore_completed");
        await refresh(); await load();
      } else {
        toast.info("No active purchases found");
      }
    } catch (e) {
      track("calm_restore_failed");
      toast.error(e.message || "Could not restore purchases");
    } finally { setBusy(false); }
  };

  const isActive = status?.active;
  const plat = platform();
  const isNative = plat !== "web";
  const showStripe = !isNative;

  const manageMembership = async () => {
    track("calm_manage_billing_clicked", { billing_model: isNative ? "native_subscription" : "recurring" });
    if (!isNative && status?.source === "stripe_subscription") {
      setBusy(true);
      try {
        const response = await api.post("/premium/portal", {
          return_url: window.location.href,
        });
        window.location.href = response.data.url;
      } catch (error) {
        toast.error(error.response?.data?.detail || "Could not open billing management");
      } finally {
        setBusy(false);
      }
      return;
    }
    const url = plat === "ios"
      ? "https://apps.apple.com/account/subscriptions"
      : plat === "android"
        ? "https://play.google.com/store/account/subscriptions"
        : null;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="premium-page">
      <div className="brutal-card bg-accent p-6 mb-6">
        <div className="flex items-center gap-3">
          <Sparkles size={32} strokeWidth={2.5} />
          <div>
            <h1 className="text-4xl font-display">HAEVEN Premium</h1>
            <p className="text-sm mt-1">Extra privacy, reflection and focus features for members.</p>
          </div>
          {isActive && <span className="ml-auto brutal-card bg-card px-3 py-1 text-xs font-semibold" data-testid="active-badge">ACTIVE · {status.tier?.toUpperCase()}</span>}
        </div>
      </div>

      {polling && (
        <div className="brutal-card bg-sage p-4 mb-6 flex items-center gap-3" data-testid="checkout-polling">
          <Loader2 className="animate-spin" size={20} /> Confirming your payment with Stripe…
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5 mb-8">
        {FEATURES.map(({ Icon, title, body }) => (
          <div key={title} className="brutal-card bg-card p-5">
            <Icon size={24} strokeWidth={2.5} />
            <h3 className="text-2xl mt-2">{title}</h3>
            <p className="mt-1 text-muted-foreground text-sm">{body}</p>
          </div>
        ))}
      </div>

      {!isActive && status && isNative && !iapAvailable() && (
        <div className="brutal-card bg-card p-6" data-testid="iap-not-available">
          <h3 className="text-2xl">Coming soon to mobile</h3>
          <p className="mt-2 text-sm text-muted-foreground">In-app purchases need to be enabled in this build. For now, visit <b>haeven.world</b> in Safari or Chrome to subscribe — your membership will sync automatically.</p>
        </div>
      )}

      {!isActive && status && (showStripe || iapAvailable()) && (
        <>
          <div className="grid md:grid-cols-2 gap-5">
            {Object.entries(status.packages).map(([id, p]) => (
              <div key={id} className={`brutal-card p-6 ${id === "calm_yearly" ? "bg-sage" : "bg-card"}`} data-testid={`plan-${id}`}>
                <div className="text-xs uppercase tracking-wider font-bold">{p.tier}</div>
                <h3 className="text-3xl mt-1">{p.label}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-5xl font-display">${p.amount}</span>
                  <span className="text-sm">/ {p.period_days === 30 ? "month" : "year"}</span>
                </div>
                <ul className="mt-4 space-y-1 text-sm">
                  <li className="flex items-center gap-2"><Check size={14} strokeWidth={3} /> Journaling + mood tagging</li>
                  <li className="flex items-center gap-2"><Check size={14} strokeWidth={3} /> Private weekly mood report</li>
                  <li className="flex items-center gap-2"><Check size={14} strokeWidth={3} /> Distraction-free mode</li>
                  <li className="flex items-center gap-2"><Check size={14} strokeWidth={3} /> Advanced privacy</li>
                  {Number(p.savings_percent) > 0 && <li className="flex items-center gap-2"><Check size={14} strokeWidth={3} /> <b>Save {p.savings_percent}% vs monthly</b></li>}
                </ul>
                <button data-testid={`subscribe-${id}-btn`} disabled={busy} onClick={() => checkout(id)}
                  className="brutal-btn bg-primary text-primary-foreground w-full mt-5 py-3 font-display text-lg disabled:opacity-60">
                  {busy ? "Loading…" : isNative || p.web_billing_model === "recurring" ? `Get ${p.period_days === 30 ? "monthly" : "yearly"}` : `Get ${p.period_days === 30 ? "30-day" : "1-year"} access`}
                </button>
                <p className="mt-3 text-xs text-muted-foreground" data-testid={`billing-terms-${id}`}>
                  {isNative || p.web_billing_model === "recurring"
                    ? "Renews automatically until canceled."
                    : `One-time purchase for ${p.period_days === 30 ? "30 days" : "one year"} of access; no automatic renewal.`}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-5 text-center text-xs text-muted-foreground space-y-2" data-testid="subscription-disclosure">
            <p>
              {isNative
                ? "Payment is charged to your store account when confirmed. Subscriptions renew automatically unless canceled at least 24 hours before the current period ends. Manage or cancel in your App Store or Google Play account settings."
                : "Web billing and renewal terms are shown with each plan before checkout."}
            </p>
            <p>
              By subscribing, you agree to the{" "}
              <Link to="/legal/terms" className="underline font-semibold">Terms of Service</Link>
              {" "}and acknowledge the{" "}
              <Link to="/legal/privacy" className="underline font-semibold">Privacy Policy</Link>.
            </p>
          </div>
          {isNative && iapAvailable() && (
            <div className="mt-4 text-center">
              <button onClick={restore} disabled={busy} data-testid="restore-purchases-btn" className="brutal-btn bg-card px-4 py-2 inline-flex items-center gap-2 text-sm">
                <RotateCcw size={14} strokeWidth={2.5} /> Restore purchases
              </button>
            </div>
          )}
        </>
      )}

      {isActive && (
        <div className="brutal-card bg-card p-6 text-center" data-testid="member-area">
          <h2 className="text-2xl mb-2">You&apos;re a Premium member ✨</h2>
          <p className="text-muted-foreground mb-4">Membership active until {new Date(status.expires_at).toLocaleDateString()}.</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button onClick={() => navigate("/journal")} className="brutal-btn bg-primary text-primary-foreground px-4 py-2" data-testid="open-journal-btn">Open journal</button>
            <button onClick={() => navigate("/weekly-report")} className="brutal-btn bg-accent px-4 py-2" data-testid="open-report-btn">Weekly report</button>
            {(isNative || status?.source === "stripe_subscription") && (
              <button onClick={manageMembership} disabled={busy} className="brutal-btn bg-card px-4 py-2 inline-flex items-center gap-2" data-testid="manage-subscription-btn">
                <ExternalLink size={14} strokeWidth={2.5} /> Manage subscription
              </button>
            )}
          </div>
          {!isNative && (
            <p className="text-xs text-muted-foreground mt-4" data-testid="web-access-disclosure">
              {status?.source === "stripe_subscription"
                ? "Your web membership renews automatically until canceled. Use Manage subscription above to change billing."
                : "Web access ends on the date shown and does not renew automatically."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
